import { useParams, useNavigate } from 'react-router-dom';
import { PromoCodeOnlyBadge } from './PromoCodeOnlyBadge';
import { useEffect, useState } from 'react';
import { ShoppingCart, Pencil } from 'lucide-react';
import { ProductVariation, CalendarCustomization, ItemPersonalization, BusinessCardCustomization, LogoOption, LOGO_THEME_LIGHT } from '../types';
import { BreadcrumbItem } from '../types';
import { useShopping } from '../contexts/ShoppingContext';
import { useClientDataContext } from '../contexts/ClientDataContext';
import { useGuestMode } from '../hooks/useGuestMode';
import { useUser } from '../contexts/UserContext';
import { QuantitySelector, Button, ImageGallery, Breadcrumb, Toast } from './ui';
import { BulkOrderTable } from './BulkOrderTable';
import { CalendarCustomizer } from './CalendarCustomizer';
import { ItemPersonalizer } from './ItemPersonalizer';
import { BusinessCardCustomizer } from './BusinessCardCustomizer';
import { CustomizeItemCustomizer } from './CustomizeItemCustomizer';
import { CopyPersonalizationButton } from './CopyPersonalizationButton';
import { ReorderRequestModal } from './ReorderRequestModal';
import { LogoService } from '../services/logoService';
import { ProductService } from '../services/productService';
import { SecondLogoOption } from '../types';
import {formatPrice} from "../utils/formatPrice.ts";
import { gtm } from '../services/gtm';

function getTextColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

export function ProductPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { state, dispatch } = useShopping();
  const { clientData } = useClientDataContext();
  const { isGuest, loading: authLoading } = useGuestMode();
  const { user } = useUser();
  const storeIsOpen = !isGuest || clientData?.store?.isOpen !== false;
  const [selectedVariation, setSelectedVariation] = useState<ProductVariation | null>(null);
  const [product, setProduct] = useState<import('../types').Product | null>(null);
  const [productLoading, setProductLoading] = useState(true);
  const [productError, setProductError] = useState<string | null>(null);

  useEffect(() => {
    if (!productId || authLoading) return;
    let cancelled = false;
    const run = async () => {
      await Promise.resolve();
      if (cancelled) return;
      setProductLoading(true);
      setProductError(null);
      try {
        const p = await ProductService.getProduct(Number(productId));
        if (!cancelled) setProduct(p);
      } catch {
        if (!cancelled) setProductError('Product not found');
      } finally {
        if (!cancelled) setProductLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [productId, authLoading]);

  const minQty = product?.minOrderQty && product.minOrderQty > 1 ? product.minOrderQty : 1;
  const maxQty = product?.maxOrderQty && product.maxOrderQty > 1 ? product.maxOrderQty : undefined;
  const [quantity, setQuantity] = useState(minQty);
  const [hoveredColor, setHoveredColor] = useState<string | null>(null);
  const [calendarCustomization, setCalendarCustomization] = useState<CalendarCustomization | undefined>();
  const [personalizations, setPersonalizations] = useState<ItemPersonalization[]>([{ text: '' }]);
  const [personalizationErrors, setPersonalizationErrors] = useState<{ text: string; placement: string }[]>([]);
  const [personalizationExpanded, setPersonalizationExpanded] = useState(false);
  const [businessCardCustomization, setBusinessCardCustomization] = useState<BusinessCardCustomization | undefined>();
  const [customizeItemCustomization, setCustomizeItemCustomization] = useState<BusinessCardCustomization | undefined>();
  const [customizerResetKey, setCustomizerResetKey] = useState(0);
  const [logoOptions, setLogoOptions] = useState<LogoOption[]>([]);
  const [selectedLogoOption, setSelectedLogoOption] = useState<LogoOption | null>(null);
  const [secondLogoOptions, setSecondLogoOptions] = useState<SecondLogoOption[]>([]);
  const [selectedSecondLogoOption, setSelectedSecondLogoOption] = useState<SecondLogoOption | null>(null);
  const [secondLogoExpanded, setSecondLogoExpanded] = useState(false);
  const [reorderModalOpen, setReorderModalOpen] = useState(false);
  const [reorderSuccessToast, setReorderSuccessToast] = useState(false);

  // For products without variations, create a default variation using product-level stock
  const variation = selectedVariation || product?.variations[0] || (
    product?.variations.length === 0 && product?.stock !== null && product?.stock !== undefined
      ? {
          id: `${product.id}-default`,
          color: "",
          size: "",
          price: product.basePrice,
          stock: product.stock,
          images: product.images
        }
      : undefined
  );

  // Build breadcrumb items
  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Home', href: '/' }
  ];

  // Get department and category from product
  if (product && product.categories.length > 0) {
    const categoryId = product.categories[0];
    const category = state.departments
      .flatMap(dept => dept.productCategories || [])
      .find(cat => cat.id === categoryId);

    if (category) {
      // Find the department that contains this category
      const department = state.departments.find(d =>
        d.productCategories?.some(c => c.id === categoryId)
      );

      if (department) {
        breadcrumbItems.push({
          label: department.name,
          href: `/?departmentId=${department.id}`
        });
      }

      const categoryHref = department
        ? `/?departmentId=${department.id}&categoryId=${category.id}`
        : `/?categoryId=${category.id}`;

      breadcrumbItems.push({
        label: category.name,
        href: categoryHref
      });
    }
  }

  if (product) {
    breadcrumbItems.push({
      label: product.name
    });
  }

  // Get current images based on hovered or selected variation
  const getCurrentImages = () => {
    // If hovering over a color, show that variation's images
    if (hoveredColor && product) {
      const hoveredVariation = product.variations.find(v =>
        v.color === hoveredColor && (selectedVariation ? v.size === selectedVariation.size : true)
      );
      if (hoveredVariation?.images && hoveredVariation.images.length > 0) {
        return hoveredVariation.images;
      }
    }

    // Otherwise show selected variation images
    if (variation?.images && variation.images.length > 0) {
      return variation.images;
    }
    return product?.images || [];
  };

  useEffect(() => {
    if (!product) return;
    const firstVariation = product.variations[0] ?? {
      id: `${product.id}-default`,
      size: '',
      color: '',
      price: product.basePrice,
      stock: product.stock ?? 0,
    };
    gtm.pushViewItem(product, firstVariation);
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!product || selectedVariation) return;
    let cancelled = false;
    const run = async () => {
      await Promise.resolve();
      if (cancelled) return;
      const color = product.variations.length > 0 ? product.variations[0].color || null : null;
      if (product.variations.length > 0) setSelectedVariation(product.variations[0]);
      const { primary, secondary } = await LogoService.getLogoOptions(product.id, color);
      if (!cancelled) {
        setLogoOptions(primary);
        setSelectedLogoOption(primary.find(o => o.isDefault) ?? primary[0] ?? null);
        setSecondLogoOptions(secondary);
      }
      const newMinQty = product.minOrderQty && product.minOrderQty > 1 ? product.minOrderQty : 1;
      if (!cancelled) setQuantity(newMinQty);
    };
    run();
    return () => { cancelled = true; };
  }, [product, selectedVariation]);

  if (productLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-pulse">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6">
          <div className="h-4 w-10 bg-gray-200 rounded" />
          <div className="h-4 w-3 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-3 bg-gray-200 rounded" />
          <div className="h-4 w-32 bg-gray-200 rounded" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Image gallery skeleton */}
          <div className="lg:max-w-md space-y-3">
            <div className="aspect-square bg-gray-200 rounded-lg" />
            <div className="flex gap-2">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="w-16 h-16 bg-gray-200 rounded-md flex-shrink-0" />
              ))}
            </div>
          </div>
          {/* Details skeleton */}
          <div className="space-y-4">
            <div className="h-7 bg-gray-200 rounded w-3/4" />
            <div className="border-t border-gray-200 pt-4">
              <div className="h-9 bg-gray-200 rounded w-28" />
            </div>
            {/* Size */}
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-12" />
              <div className="flex gap-2">
                {[1, 2, 3, 4].map(i => <div key={i} className="h-9 w-16 bg-gray-200 rounded-lg" />)}
              </div>
            </div>
            {/* Color */}
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-14" />
              <div className="flex gap-2">
                {[1, 2, 3].map(i => <div key={i} className="h-9 w-24 bg-gray-200 rounded-lg" />)}
              </div>
            </div>
            {/* Qty */}
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-20" />
              <div className="h-10 w-36 bg-gray-200 rounded-lg" />
            </div>
            <div className="border-t border-gray-200 pt-4">
              <div className="h-11 bg-gray-200 rounded-lg w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (productError || !product) {
    navigate('/');
    return null;
  }

  const totalPersonalizationFee = product.personalizationEnabled && personalizationExpanded
    ? (Number(product.personalizationFee) || 0) * personalizations.length
    : 0;

  const handlePersonalizationChange = (index: number, value: ItemPersonalization) => {
    setPersonalizations(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    setPersonalizationErrors(prev => {
      const next = [...prev];
      if (!next[index]) return next;
      next[index] = {
        text: value.text.trim() ? '' : next[index].text,
        placement: value.placement ? '' : next[index].placement,
      };
      return next;
    });
  };

  const handleAddPersonalization = () => {
    setPersonalizations(prev => [...prev, { text: '' }]);
  };

  const handleRemovePersonalization = (index: number) => {
    setPersonalizations(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddToCart = () => {
    if (variation) {
      if (product.isPrintDetail && product.printDetailType === 'calendar' && !calendarCustomization) {
        alert('Please customize your calendar before adding to cart');
        return;
      }
      if (product.isPrintDetail && product.printDetailType === 'business_card' && !businessCardCustomization) {
        alert('Please customize your business card before adding to cart');
        return;
      }
      if (product.isPrintDetail && product.printDetailType === 'customize_item' && !customizeItemCustomization) {
        alert('Please customize your item before adding to cart');
        return;
      }
      if (product.personalizationEnabled && personalizationExpanded) {
        const hasPlacementField = (product.personalizationPlacements?.length ?? 0) > 0;
        const errors = personalizations.map(p => ({
          text: p.text.trim() ? '' : 'Please add personalization text',
          placement: hasPlacementField && !p.placement ? 'Please select a placement' : '',
        }));
        if (errors.some(e => e.text || e.placement)) {
          setPersonalizationErrors(errors);
          document.getElementById('personalization-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
      const filledPersonalizations = product.personalizationEnabled && personalizationExpanded
        ? personalizations.map(p => ({ ...p, fee: product.personalizationFee ?? undefined }))
        : undefined;
      gtm.pushAddToCart(product, variation, quantity);
      dispatch({
        type: 'ADD_TO_CART',
        payload: { product, variation, quantity, calendarCustomization, personalizations: filledPersonalizations, businessCardCustomization, customizeItemCustomization, logoOption: selectedLogoOption, secondLogoOption: selectedSecondLogoOption }
      });
    }
  };

  const handleBulkAddToCart = (items: Array<{ variation: ProductVariation; quantity: number }>) => {
    items.forEach(({ variation, quantity }) => {
      gtm.pushAddToCart(product, variation, quantity);
      dispatch({
        type: 'ADD_TO_CART',
        payload: { product, variation, quantity }
      });
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb items={breadcrumbItems} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Image Gallery */}
        <div className="lg:max-w-md">
          <ImageGallery
            images={getCurrentImages()}
            alt={product.name}
            className="rounded-lg"
            showThumbnails={true}
            enableKeyboardNavigation={true}
          />
        </div>

        {/* Product Details */}
        <div className="space-y-4">
          <div>
            <div className="flex items-start justify-between gap-2 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">
                {product.name}
              </h1>
              {user?.globalAdmin && (
                <a
                  href={`/adminpanel/resources/products/${product.id}/edit`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-shrink-0 inline-flex items-center justify-center p-1.5 rounded-md border border-gray-200 text-gray-400 hover:text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  title="Edit in Admin"
                >
                  <Pencil className="w-4 h-4" />
                </a>
              )}
            </div>
            {product.promoCodeOnly && (
              <PromoCodeOnlyBadge className="mb-3" />
            )}
          </div>

          {/* Price */}
          <div className="border-t border-gray-200 pt-4">
            <span className="text-3xl font-bold text-gray-900">
              ${formatPrice(Number(variation?.price ?? product.basePrice ?? 0) + Number(totalPersonalizationFee) + Number(selectedSecondLogoOption?.price ?? 0))}
            </span>
            {personalizationExpanded && totalPersonalizationFee > 0 && (
              <span className="text-sm text-gray-500 ml-2">
                includes ${formatPrice(totalPersonalizationFee)} personalization
              </span>
            )}
            {selectedSecondLogoOption && (
              <span className="text-sm text-gray-500 ml-2">
                includes +${selectedSecondLogoOption.price.toFixed(2)} second logo
              </span>
            )}
          </div>

          {/* Customer-facing one-time charges (flat per cart line item, not per unit) */}
          {product.customerCharges?.map((charge, i) => (
            <div key={i} className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">
                {charge.name || 'One Time Charge'}:
              </span>{' '}
              ${formatPrice(charge.amount)}
            </div>
          ))}

          {/* Production Timeline */}
          {product.productionTimeline && (
            <div className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">Production Time:</span> {product.productionTimeline}
            </div>
          )}

          {/* Variations */}
          <div className="space-y-4">
            {/* Size Selection */}
            {product.variations
                    .filter(v => v.size.trim() !== '')
                    .some(v => v.size !== 'Standard')
                && (
              <div>
                <h3 className="text-base font-medium text-gray-900 mb-2">Size</h3>
                <div className="grid grid-cols-4 gap-2">
                  {Array.from(
                      new Set(product.variations
                          .filter(v => v.size.trim() !== '')
                          .map(v => v.size))
                  ).map(size => {
                    // Check if any variation with this size has stock or if backorder/on-demand is enabled
                    const hasStock = product.allowBackorder || product.allowOnDemand ||
                      product.variations.some(v => v.size === size && v.stock > 0);
                    const showOutOfStock = !hasStock && !product.allowOnDemand;

                    return (
                      <button
                        key={size}
                        onClick={() => {
                          const newVariation = product.variations.find(v =>
                            v.size === size && (selectedVariation ? v.color === selectedVariation.color : true)
                          ) || product.variations.find(v => v.size === size);
                          setSelectedVariation(newVariation || null);
                          const newColor = newVariation?.color || null;
                          if (newColor !== (selectedVariation?.color || null)) {
                            LogoService.getLogoOptions(product.id, newColor).then(({ primary, secondary }) => {
                              setLogoOptions(primary);
                              setSelectedLogoOption(primary.find(o => o.isDefault) ?? primary[0] ?? null);
                              setSecondLogoOptions(secondary);
                              setSelectedSecondLogoOption(null);
                              setSecondLogoExpanded(false);
                            });
                          }
                        }}
                        disabled={!hasStock}
                        className={`py-2 px-3 text-sm border rounded-lg font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          !hasStock
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                            : variation?.size === size
                            ? 'border-blue-600 bg-blue-50 text-blue-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        aria-label={`Select size ${size}${showOutOfStock ? ' (out of stock)' : ''}`}
                        aria-pressed={variation?.size === size}
                        aria-disabled={!hasStock}
                      >
                        {!hasStock ? <span className="line-through">{size}</span> : size}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {(() => {
              const selectedSize = variation?.size;
              const colors = Array.from(new Set(product.variations.map(v => v.color)))
                .filter(c => c && c !== 'Default')
                .filter(c => !selectedSize || product.variations.some(v => v.color === c && v.size === selectedSize));
              return colors.length > 0 ? (
                <div>
                  <h3 className="text-base font-medium text-gray-900 mb-2">Color</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {colors.map(color => {
                      const availableForSize = !selectedSize || product.variations.some(v => v.color === color && v.size === selectedSize);
                      const hasStock = availableForSize && (product.allowBackorder || product.allowOnDemand ||
                        product.variations.some(v => v.color === color && (!selectedSize || v.size === selectedSize) && v.stock > 0));
                      const showOutOfStock = !hasStock && !product.allowOnDemand;

                      const bgColor = product.variations.find(v => v.color === color)?.hex || '#E5E7EB';
                      const textColor = getTextColor(bgColor);

                      return (
                        <button
                          key={color}
                          onClick={() => {
                            const newVariation = product.variations.find(v =>
                              v.color === color && (selectedVariation ? v.size === selectedVariation.size : true)
                            ) || product.variations.find(v => v.color === color);
                            setSelectedVariation(newVariation || null);
                            LogoService.getLogoOptions(product.id, color).then(({ primary, secondary }) => {
                              setLogoOptions(primary);
                              setSelectedLogoOption(primary.find(o => o.isDefault) ?? primary[0] ?? null);
                              setSecondLogoOptions(secondary);
                              setSelectedSecondLogoOption(null);
                              setSecondLogoExpanded(false);
                            });
                          }}
                          onMouseEnter={() => !hasStock ? null : setHoveredColor(color)}
                          onMouseLeave={() => setHoveredColor(null)}
                          disabled={!hasStock}
                          style={{
                            backgroundColor: hasStock ? bgColor : '#F3F4F6',
                            color: hasStock ? textColor : '#9CA3AF',
                            borderColor: variation?.color === color ? '#2563EB' : '#D1D5DB',
                            opacity: hasStock ? 1 : 0.6
                          }}
                          className={`py-2 px-3 text-sm border rounded-lg font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                            !hasStock
                              ? 'cursor-not-allowed'
                              : variation?.color === color
                              ? 'ring-2 ring-blue-600'
                              : 'hover:border-gray-400'
                          }`}
                          aria-label={`Select ${color} color${showOutOfStock ? ' (out of stock)' : ''}`}
                          aria-pressed={variation?.color === color}
                          aria-disabled={!hasStock}
                        >
                          {!hasStock ? <span className="line-through">{color}</span> : color}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null;
            })()}
          </div>

          {/* Logo Selection — only shown when product has logo options */}
          {logoOptions.length > 0 && (
            <div>
              <h3 className="text-base font-medium text-gray-900 mb-2">Logo</h3>
              <div className="grid grid-cols-3 gap-2">
                {logoOptions.map(option => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setSelectedLogoOption(option)}
                    className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      selectedLogoOption?.id === option.id
                        ? 'border-blue-600 bg-blue-50 text-blue-700 ring-2 ring-blue-600'
                        : 'border-gray-300 hover:border-gray-400 text-gray-700'
                    }`}
                    aria-pressed={selectedLogoOption?.id === option.id}
                  >
                    {option.imageUrl ? (
                      <img
                        src={option.imageUrl}
                        alt={option.name}
                        className="h-16 w-auto max-w-full object-contain"
                      />
                    ) : (
                      <div className="h-16 w-full flex items-center justify-center bg-gray-100 rounded text-gray-400 text-xs">
                        No image
                      </div>
                    )}
                    <span className="text-center leading-tight">{option.name}</span>
                    {option.theme != null && option.theme !== 0 && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        option.theme === LOGO_THEME_LIGHT
                          ? 'bg-gray-700 text-white'
                          : 'bg-gray-100 text-gray-600 border border-gray-300'
                      }`}>
                        {option.theme === LOGO_THEME_LIGHT ? 'Light' : 'Dark'}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              {selectedLogoOption && (
                <p className="mt-1 text-xs text-gray-500">
                  Selected: <span className="font-medium">{selectedLogoOption.name}</span>
                </p>
              )}
            </div>
          )}

          {/* Second Logo — expand/collapse like personalization */}
          {secondLogoOptions.length > 0 && (
            <div>
              {!secondLogoExpanded ? (
                <button
                  type="button"
                  onClick={() => { setSecondLogoExpanded(true); setSelectedSecondLogoOption(secondLogoOptions[0] ?? null); }}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2"
                >
                  + Add Second Logo
                  {secondLogoOptions.some(o => o.price > 0) && (
                    <span className="text-gray-500">
                      from +${Math.min(...secondLogoOptions.map(o => o.price)).toFixed(2)}/unit
                    </span>
                  )}
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <h3 className="text-base font-medium text-gray-900">Second Logo</h3>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedSecondLogoOption(null);
                        setSecondLogoExpanded(false);
                      }}
                      className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-medium transition-colors focus:outline-none"
                      aria-label="Remove second logo"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      Remove
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {secondLogoOptions.map(option => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setSelectedSecondLogoOption(option)}
                        className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          selectedSecondLogoOption?.id === option.id
                            ? 'border-blue-600 bg-blue-50 text-blue-700 ring-2 ring-blue-600'
                            : 'border-gray-300 hover:border-gray-400 text-gray-700'
                        }`}
                        aria-pressed={selectedSecondLogoOption?.id === option.id}
                      >
                        {option.imageUrl ? (
                          <img
                            src={option.imageUrl}
                            alt={option.name}
                            className="h-16 w-auto max-w-full object-contain"
                          />
                        ) : (
                          <div className="h-16 w-full flex items-center justify-center bg-gray-100 rounded text-gray-400 text-xs">
                            No image
                          </div>
                        )}
                        <span className="text-center leading-tight">{option.name}</span>
                        <div className="flex items-center gap-1 flex-wrap justify-center">
                          {option.price > 0 && (
                            <span className="text-xs font-semibold text-green-700">+${option.price.toFixed(2)}</span>
                          )}
                          {option.theme != null && option.theme !== 0 && (
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                              option.theme === LOGO_THEME_LIGHT
                                ? 'bg-gray-700 text-white'
                                : 'bg-gray-100 text-gray-600 border border-gray-300'
                            }`}>
                              {option.theme === LOGO_THEME_LIGHT ? 'Light' : 'Dark'}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  {selectedSecondLogoOption && (
                    <p className="mt-1 text-xs text-gray-500">
                      Selected: <span className="font-medium">{selectedSecondLogoOption.name}</span>
                      {selectedSecondLogoOption.price > 0 && ` (+$${selectedSecondLogoOption.price.toFixed(2)})`}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Quantity */}
          <div>
            <h3 className="text-base font-medium text-gray-900 mb-2">
              Quantity
              {((product.minOrderQty != null && product.minOrderQty > 1) ||
                  (product.maxOrderQty != null && product.maxOrderQty > 1)) && (
                  <span className="ml-2 text-sm text-gray-500">
                    (
                    {product.minOrderQty != null && product.minOrderQty > 1 && `Min: ${product.minOrderQty}`}
                    {product.minOrderQty != null &&
                        product.minOrderQty > 1 &&
                        product.maxOrderQty != null &&
                        product.maxOrderQty > 1 &&
                        ' - '}
                    {product.maxOrderQty != null && product.maxOrderQty > 1 && `Max: ${product.maxOrderQty}`}
                    )
                  </span>
              )}
            </h3>
            <QuantitySelector
              quantity={quantity}
              onQuantityChange={setQuantity}
              min={minQty}
              max={maxQty}
              size="md"
              stock={variation?.stock}
              showStock={true}
              allowManualInput={true}
              allowOnDemand={product.allowOnDemand}
            />
          </div>

          {/* Personalization */}
          {product.personalizationEnabled && (
            <div id="personalization-section">
              {!personalizationExpanded ? (
                <button
                  type="button"
                  onClick={() => setPersonalizationExpanded(true)}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2"
                >
                  + Add Personalization
                  {product.personalizationFee != null && product.personalizationFee > 0 && (
                    <span>+${formatPrice(product.personalizationFee)}/unit</span>
                  )}
                </button>
              ) : (
                <div className="space-y-3">
                  {personalizations.map((p, i) => (
                    <ItemPersonalizer
                      key={i}
                      instructions={i === 0 ? product.personalizationInstructions : null}
                      fee={product.personalizationFee}
                      charLimit={product.personalizationCharLimit}
                      placements={product.personalizationPlacements}
                      value={p}
                      onChange={(val) => handlePersonalizationChange(i, val)}
                      label={personalizations.length > 1 ? `Personalization #${i + 1}` : undefined}
                      textError={personalizationErrors[i]?.text || null}
                      placementError={personalizationErrors[i]?.placement || null}
                      onRemove={
                        personalizations.length > 1
                          ? () => handleRemovePersonalization(i)
                          : () => {
                              setPersonalizationExpanded(false);
                              setPersonalizations([{ text: '' }]);
                              setPersonalizationErrors([]);
                            }
                      }
                    />
                  ))}
                  <button
                    type="button"
                    onClick={handleAddPersonalization}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                  >
                    + Add another personalization
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Add to Cart Button */}
          <div className="border-t border-gray-200 pt-4 space-y-2">
            <Button
              onClick={handleAddToCart}
              disabled={!storeIsOpen || !variation || (!product.allowBackorder && !product.allowOnDemand && variation.stock < quantity)}
              variant="primary"
              size="md"
              fullWidth={true}
              icon={<ShoppingCart className="w-5 h-5" />}
            >
              {storeIsOpen ? 'Add to Cart' : 'Store Closed'}
            </Button>

            {clientData?.enableRequestReorder && (user?.clientAdmin || user?.globalAdmin || user?.distributorAdmin) && (
              <Button
                onClick={() => setReorderModalOpen(true)}
                variant="secondary"
                size="md"
                fullWidth={true}
              >
                Request Reorder
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Calendar Customization */}
      {product.isPrintDetail && product.printDetailType === 'calendar' && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Customize Your Calendar</h2>
            <CopyPersonalizationButton
              targetProductType="calendar"
              targetProduct={product}
              currentDestValue={calendarCustomization}
              onApply={(merged) => {
                setCalendarCustomization(merged as CalendarCustomization);
                setCustomizerResetKey(k => k + 1);
              }}
            />
          </div>
          <CalendarCustomizer
            key={customizerResetKey}
            onCustomizationChange={setCalendarCustomization}
            initialCustomization={calendarCustomization}
            previewImageUrl={product.printImages?.frontForReview}
            style={product.printOnDemandStyle}
            fields={product.printOnDemandFields}
          />
        </div>
      )}

      {/* Business Card Customization */}
      {product.isPrintDetail && product.printDetailType === 'business_card' && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Customize Your Business Card</h2>
            <CopyPersonalizationButton
              targetProductType="business_card"
              targetProduct={product}
              currentDestValue={businessCardCustomization}
              onApply={(merged) => {
                setBusinessCardCustomization(merged as BusinessCardCustomization);
                setCustomizerResetKey(k => k + 1);
              }}
            />
          </div>
          <BusinessCardCustomizer
            key={customizerResetKey}
            onCustomizationChange={setBusinessCardCustomization}
            initialCustomization={businessCardCustomization}
            productImages={product.images}
            previewImage={product.printImages?.frontForReview}
            style={product.printOnDemandStyle}
            fields={product.printOnDemandFields}
            options={product.printOnDemandOptions}
            useCardBuilder={product.useCardBuilder}
            cardTemplateId={product.cardTemplateId}
          />
        </div>
      )}

      {/* Customize Item */}
      {product.isPrintDetail && product.printDetailType === 'customize_item' && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Customize Your Item</h2>
            <CopyPersonalizationButton
              targetProductType="customize_item"
              targetProduct={product}
              currentDestValue={customizeItemCustomization}
              onApply={(merged) => {
                setCustomizeItemCustomization(merged as BusinessCardCustomization);
                setCustomizerResetKey(k => k + 1);
              }}
            />
          </div>
          <CustomizeItemCustomizer
            key={customizerResetKey}
            onCustomizationChange={setCustomizeItemCustomization}
            initialCustomization={customizeItemCustomization}
            productImages={product.images}
            previewImage={product.printImages?.frontForReview}
            fields={product.printOnDemandFields}
            options={product.printOnDemandOptions}
          />
        </div>
      )}

      {/* Full Description below image */}
      <div className="mt-6">
        <h3 className="text-base font-medium text-gray-900 mb-2">Description</h3>
        <div
          className="text-sm text-gray-600 leading-relaxed [&>p]:mb-4 [&>ul]:list-disc [&>ul]:ml-6 [&>ol]:list-decimal [&>ol]:ml-6 [&>strong]:font-semibold [&>em]:italic"
          dangerouslySetInnerHTML={{ __html: product.description }}
        />
      </div>

      {/* Bulk Order Table */}
      {product.variations.length > 0 && (
        <div className="mt-8">
          <BulkOrderTable product={product} onAddToCart={handleBulkAddToCart} disabled={!storeIsOpen} />
        </div>
      )}

      <ReorderRequestModal
        isOpen={reorderModalOpen}
        productId={product.id}
        onClose={() => setReorderModalOpen(false)}
        onSuccess={() => {
          setReorderModalOpen(false);
          setReorderSuccessToast(true);
        }}
      />

      {reorderSuccessToast && (
        <Toast
          message="Your reorder request has been submitted. Your account rep will be in touch."
          productName={product.name}
          productImage={product.images[0] ?? ''}
          type="success"
          duration={5000}
          onClose={() => setReorderSuccessToast(false)}
        />
      )}
    </div>
  );
}
